if (!customElements.get('announcement-marquee')) {
  customElements.define('announcement-marquee', class extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.announcement-marquee__track');
    if (!this.track) return;

    // Only the header announcement bar should control --announcement-height.
    // Other marquee usages (e.g. trust bar) must not overwrite this variable.
    this._announcementBar = this.closest('.announcement-marquee-bar');
    this._managesAnnouncementHeight = !!this._announcementBar;

    this.pps = parseInt(this.track.dataset.speed, 10) || 80;
    this._originalHTML = this.track.innerHTML;
    this._fillTrack();
    this.setDuration();
    this.setAnnouncementHeight();

    this._resizeTimer = null;
    this._onResize = () => {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => {
        this._fillTrack();
        this.setDuration(); 
        if (this._managesAnnouncementHeight) this.setAnnouncementHeight();
      }, 150);
    };
    window.addEventListener('resize', this._onResize);

    if (this._managesAnnouncementHeight) {
      // Keep height in sync with runtime changes (font load, admin bar, etc.)
      this._barResizeObserver = new ResizeObserver(() => this.setAnnouncementHeight());
      this._barResizeObserver.observe(this._announcementBar);
      // Recalculate after initial paint to avoid early/incorrect measurement.
      requestAnimationFrame(() => this.setAnnouncementHeight());
    }
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this._onResize);
    if (this._barResizeObserver) {
      this._barResizeObserver.disconnect();
      this._barResizeObserver = null;
    }

    // Restore from another active announcement bar instance if present.
    if (this._managesAnnouncementHeight) {
      const remaining = document.querySelector('.announcement-marquee-bar announcement-marquee');
      if (remaining && typeof remaining.setAnnouncementHeight === 'function') {
        remaining.setAnnouncementHeight();
      } else {
        document.documentElement.style.removeProperty('--announcement-height');
      }
    }
  }

  // Ensure track has enough copies to cover at least 2× the container width
  _fillTrack() {
    const containerWidth = this.offsetWidth || window.innerWidth;
    // Reset to one set first
    this.track.innerHTML = this._originalHTML;
    // Ensure layout has settled
    void this.track.offsetWidth;
    // Measure one set's width
    const originalSetWidth = this.track.scrollWidth;
    if (originalSetWidth <= 0) return;

    // Append copies until the total content width is at least
    // containerWidth + one original set width. This guarantees
    // there is always enough content to scroll by exactly one
    // set width without revealing empty space.
    let totalWidth = originalSetWidth;
    let copies = 1;
    // Safety guard to avoid infinite loops
    const MAX_COPIES = 50;
    while (totalWidth < containerWidth + originalSetWidth && copies < MAX_COPIES) {
      this.track.insertAdjacentHTML('beforeend', this._originalHTML);
      copies += 1;
      // Force reflow and measure new total
      void this.track.offsetWidth;
      totalWidth = this.track.scrollWidth;
    }
    this._copies = copies;
    this._oneSetWidth = originalSetWidth;
  }

  setDuration() {
    // Use the original one-set width (measured before duplication)
    const oneSetWidth = this._oneSetWidth || (this.track.scrollWidth / (this._copies || 1));
    const duration = oneSetWidth / this.pps;
    this.track.style.animationDuration = duration.toFixed(2) + 's';
    // Translate by exactly one set width in px for seamless looping
    this.track.style.setProperty('--marquee-translate', `-${oneSetWidth.toFixed(2)}px`);
  }

  setAnnouncementHeight() {
    if (!this._managesAnnouncementHeight || !this._announcementBar) return;
    const height = Math.ceil(this._announcementBar.getBoundingClientRect().height);
    if (height > 0) {
      document.documentElement.style.setProperty('--announcement-height', `${height}px`);
    }
  }
  });
}
